import { useState, useEffect, useRef, useCallback } from 'react';
import AnimateHeight from 'react-animate-height';
import { Text, RichText, Field } from '@sitecore-jss/sitecore-jss-react';
import { NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import styles from './TeamListingsCard.module.scss';

interface TeamListingsCardProps {
  fields: {
    image?: {
      alt: string;
      src: string;
    };
    title?: Field<string>;
    position?: Field<string>;
    description?: Field<string>;
  };
  index?: number;
  className?: string;
}

export default function TeamListingsCard({
  fields,
  index = 0,
  className = '',
}: TeamListingsCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const { t } = useI18n();
  const collapseTargetRef = useRef<HTMLDivElement | null>(null);

  const resizeHandler = useCallback(() => {
    const el = collapseTargetRef.current;
    if (!el) return;

    const viewportWidth = window.innerWidth;
    setMaxHeight(viewportWidth <= 767 ? 240 : 270);

    setOverflow(el.scrollHeight > maxHeight);
  }, [maxHeight]);

  useEffect(() => {
    if (collapseTargetRef.current) {
      resizeHandler();
    }
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [resizeHandler]);

  return (
    <section className={`container default-section-padding ${className}`}>
      <div
        className={`d-flex flex-column flex-md-row align-items-start relative ${styles['background-md-white']}`}
      >
        <div className={`${styles['team-listing-card__thumbnail']} col-4`}>
          <NextImage field={fields.image} width={345} height={345} />
        </div>

        <div className="col-8 w-100 d-flex flex-column flex-fill mt-md-2 p-3 p-md-0">
          <Text tag="h4" field={fields.title} className="flex-shrink-0 h4" />
          <Text
            tag="p"
            field={fields.position}
            className="flex-shrink-0 subheader-2 my-3 my-md-2"
          />

          <AnimateHeight duration={300} height={isExpanded ? 'auto' : maxHeight}>
            <div ref={collapseTargetRef}>
              <RichText
                field={fields.description}
                id={`cardContent-${index}`}
                className={`flex-shrink-1 body-default text-grey overflow ${styles['rich-text__additional']}`}
              />
            </div>
          </AnimateHeight>

          {overflow && (
            <div className="flex-shrink-0">
              <button
                className={`btn ${styles['show-btn']} border-0 p-0 collapsed background-to-change ${
                  styles['rich-text__additional-toggler']
                } ${isExpanded ? styles['is-expanded'] : ''}`}
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                <span className="more">{isExpanded ? t('showLess') : t('showMore')}</span>
                <span className={styles['chevron']}>
                  <i className={'bi bi-chevron-down'} aria-hidden={true} />
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
